"use client";

import { useEffect, useState } from "react";
import { parseBrodtext } from "@/lib/nyheter-format";
import BuggIkon from "@/components/layout/BuggIkon";

export interface NyhetVisning {
  id: string;
  rubrik: string;
  oneLiner: string;
  brodtext: string;
  publiceradAt: string;
  bild: { src: string; alt: string } | null;
  /**
   * Audience badge at the start of the one-liner. Set only for viewers who
   * see a mixed list (lärare/skoladmin/admin); elever see only their own news
   * and need no badge.
   */
  malgruppTagg?: "elev" | "larare";
}

interface Props {
  nyheter: NyhetVisning[];
  /** Called once when the user dismisses the popup (button, Escape, backdrop). */
  onStang: () => void;
}

function datum(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// Presentational news popup: one news item at a time, dot pagination when
// several are unseen. Pure — data in, onStang out — so the demo page
// (/nyheter-test) can render it forced-open with arbitrary items.
export default function NyhetsPopupVy({ nyheter, onStang }: Props) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onStang();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onStang]);

  if (nyheter.length === 0) return null;
  const nyhet = nyheter[Math.min(index, nyheter.length - 1)];
  const sista = index >= nyheter.length - 1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="nyhets-popup-rubrik"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        background: "color-mix(in oklch, var(--color-text) 32%, transparent)",
      }}
      onClick={onStang}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-xl border p-6 sm:p-8 motion-safe:animate-[nyhet-in_240ms_var(--ease-out-quart)]"
        style={{
          background: "var(--color-bg-elevated)",
          borderColor: "var(--color-border)",
          boxShadow: "var(--shadow-lg)",
        }}
      >
        <div className="flex items-baseline justify-between gap-4">
          <p
            className="text-xs font-medium uppercase tracking-[0.14em]"
            style={{ color: "var(--color-accent)" }}
          >
            Nytt i vektr
          </p>
          <p className="text-xs" style={{ color: "var(--color-text-subtle)" }}>
            {datum(nyhet.publiceradAt)}
          </p>
        </div>

        {nyhet.bild && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={nyhet.bild.src}
            alt={nyhet.bild.alt}
            className="mt-4 w-full aspect-[4/3] sm:aspect-[16/9] object-cover rounded-lg border"
            style={{ borderColor: "var(--color-border)" }}
          />
        )}

        <h2
          id="nyhets-popup-rubrik"
          className="font-heading mt-5 text-3xl font-semibold leading-[1.2]"
          style={{ color: "var(--color-text)" }}
        >
          {nyhet.rubrik}
        </h2>

        <p
          className="font-heading mt-2 text-lg italic leading-snug"
          style={{ color: "var(--color-text-muted)" }}
        >
          {nyhet.malgruppTagg && (
            <span
              className="not-italic font-sans inline-block align-[3px] mr-2 px-2 py-px rounded-full text-[10px] font-medium uppercase tracking-[0.06em]"
              style={{
                background: "var(--color-warning-soft)",
                color: "var(--color-text)",
              }}
            >
              {nyhet.malgruppTagg === "larare" ? "För lärare" : "För elever"}
            </span>
          )}
          {nyhet.oneLiner}
        </p>

        <div className="mt-4 space-y-3">
          {parseBrodtext(nyhet.brodtext).map((stycke, i) => (
            <p key={i} className="text-base leading-[1.7]" style={{ color: "var(--color-text)" }}>
              {stycke.map((span, j) =>
                span.ikon === "bugg" ? (
                  <BuggIkon
                    key={j}
                    className="inline-block align-[-4px] w-5 h-5 ml-1"
                  />
                ) : span.bold ? (
                  <strong key={j} className="font-semibold">
                    {span.text}
                  </strong>
                ) : (
                  <span key={j}>{span.text}</span>
                ),
              )}
            </p>
          ))}
        </div>

        <div className="mt-6 flex items-center gap-3">
          {nyheter.length > 1 && (
            <div className="flex items-center gap-1.5" aria-hidden="true">
              {nyheter.map((n, i) => (
                <span
                  key={n.id}
                  className="h-1.5 w-1.5 rounded-full transition-colors"
                  style={{
                    background:
                      i === index ? "var(--color-text)" : "var(--color-border-strong)",
                  }}
                />
              ))}
            </div>
          )}
          {nyheter.length > 1 && (
            <p className="text-xs" style={{ color: "var(--color-text-subtle)" }}>
              {index + 1} av {nyheter.length}
            </p>
          )}

          <div className="ml-auto flex items-center gap-2">
            {index > 0 && (
              <button
                type="button"
                onClick={() => setIndex((i) => Math.max(0, i - 1))}
                title="Visa föregående nyhet"
                className="rounded-md border px-4 py-2 text-sm font-medium transition-colors hover:bg-[var(--color-bg-card)]"
                style={{
                  borderColor: "var(--color-border-strong)",
                  color: "var(--color-text)",
                }}
              >
                Föregående
              </button>
            )}
            {!sista ? (
              <button
                type="button"
                onClick={() => setIndex((i) => Math.min(nyheter.length - 1, i + 1))}
                title="Visa nästa nyhet"
                className="rounded-md px-4 py-2 text-sm font-medium transition-opacity hover:opacity-85"
                style={{
                  background: "var(--color-text)",
                  color: "var(--color-bg-elevated)",
                }}
              >
                Nästa
              </button>
            ) : (
              <button
                type="button"
                onClick={onStang}
                title="Stäng nyheterna, de visas inte igen"
                className="rounded-md px-4 py-2 text-sm font-medium transition-opacity hover:opacity-85"
                style={{
                  background: "var(--color-text)",
                  color: "var(--color-bg-elevated)",
                }}
              >
                Okej
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
